/**
 * useObjectUrlRegistry — the local object URLs the notes session shows (a
 * staged frame, a staged image's thumbnail). Each is minted or adopted here,
 * revoked when its holder lets it go, and every one still held is revoked on
 * unmount. All of it goes through the UI package's one object-URL seam.
 */
import { useEffect, useMemo, useRef } from 'react';

import { createObjectUrl, revokeObjectUrl } from '../../../../../lib/media/objectUrl';

export interface ObjectUrlRegistry {
  /** Mints a URL for `blob`, held until it's revoked or the session unmounts. */
  create(blob: Blob): string;
  /** Takes over a URL minted elsewhere (a picked image's preview). */
  adopt(url: string | undefined): void;
  /** Releases a URL this registry holds; anything else is left alone. */
  revoke(url: string | undefined): void;
  /** False once the session unmounted — async work must not mint a URL the cleanup has already missed. */
  isMounted(): boolean;
}

export function useObjectUrlRegistry(): ObjectUrlRegistry {
  const urlsRef = useRef(new Set<string>());
  const mountedRef = useRef(false);

  useEffect(() => {
    const urls = urlsRef.current;
    mountedRef.current = true;
    return function revokeOutstandingUrls() {
      mountedRef.current = false;
      urls.forEach((url) => revokeObjectUrl(url));
      urls.clear();
    };
  }, []);

  return useMemo<ObjectUrlRegistry>(() => {
    function adopt(url: string | undefined): void {
      if (!url) return;
      urlsRef.current.add(url);
    }
    function create(blob: Blob): string {
      const url = createObjectUrl(blob);
      adopt(url);
      return url;
    }
    function revoke(url: string | undefined): void {
      if (!url || !urlsRef.current.has(url)) return;
      revokeObjectUrl(url);
      urlsRef.current.delete(url);
    }
    function isMounted(): boolean {
      return mountedRef.current;
    }
    return { create, adopt, revoke, isMounted };
  }, []);
}
